import * as Collapsible from "@radix-ui/react-collapsible";
import { useEffect, useRef } from "react";
import { LuX } from "react-icons/lu";
import CreatableSelect from "react-select/creatable";
import { NOTIFICATION_COLUMN_FILTERS } from "../../constants";
import { useColumnFilters } from "./useColumnFilters";
import { useFilterSelectInput } from "./useFilterSelectInput";
import "./NotificationColumnFilters.scss";

/**
 * Persistent active filter chips and expandable column-filter builder for the Notifications list.
 * Chip-strip and panel use Radix Collapsible for height enter/exit animations.
 *
 * @param {object} props
 * @param {Record<string, string>} props.filters Applied column filters.
 * @param {(filters: Record<string, string>) => void} props.onFiltersChange Applies filters to the paginated query key.
 * @param {boolean} props.expanded Whether the filter builder is expanded.
 * @param {Record<string, unknown>[]} props.notifications Loaded notification rows used for local option suggestions.
 * @returns {import('react').JSX.Element}
 */
const NotificationColumnFilters = ({
    filters,
    onFiltersChange,
    expanded,
    notifications = [],
}) => {
    const {
        activeFilters,
        activeEntries,
        hasActiveFilters,
        setFilterValue,
        removeFilter,
        clearAllFilters,
    } = useColumnFilters(filters, onFiltersChange);

    const {
        optionGroupsByKey,
        selectMenuProps,
        getSelectValue,
        getInputValue,
        formatCreateLabel,
        handleInputChange,
        handleSelectChange,
        handleCreateOption,
        handleSelectKeyDown,
    } = useFilterSelectInput({
        notifications,
        activeFilters,
        setFilterValue,
    });

    const lastChipEntriesRef = useRef(activeEntries);

    useEffect(() => {
        if (activeEntries.length > 0) {
            lastChipEntriesRef.current = activeEntries;
        }
    }, [activeEntries]);

    const entriesForChips = hasActiveFilters
        ? activeEntries
        : lastChipEntriesRef.current;

    return (
        <div className="notification-column-filters">
            <Collapsible.Root
                open={expanded}
                id="notification-column-filters-panel"
            >
                <Collapsible.Content className="notification-column-filters__panel-collapsible">
                    <div className="notification-column-filters__panel-shell">
                        <div
                            className="notification-column-filters__panel"
                            aria-hidden={!expanded}
                        >
                            <div className="notification-column-filters__panel-title">
                                Add column filters
                            </div>
                            <div className="notification-column-filters__fields">
                                {NOTIFICATION_COLUMN_FILTERS.map(
                                    ({ key, label, placeholder }) => (
                                        <div
                                            key={key}
                                            className="notification-column-filters__field"
                                        >
                                            <label htmlFor={`notification-column-filter-${key}`}>
                                                {label}
                                            </label>
                                            <CreatableSelect
                                                inputId={`notification-column-filter-${key}`}
                                                className="notification-column-filters__select"
                                                classNamePrefix="notification-column-filters-select"
                                                options={optionGroupsByKey[key] ?? []}
                                                value={getSelectValue(key)}
                                                inputValue={getInputValue(key)}
                                                placeholder={placeholder}
                                                {...selectMenuProps}
                                                formatCreateLabel={formatCreateLabel}
                                                onInputChange={(value, meta) =>
                                                    handleInputChange(
                                                        key,
                                                        value,
                                                        meta,
                                                    )
                                                }
                                                onChange={(option) =>
                                                    handleSelectChange(
                                                        key,
                                                        option,
                                                    )
                                                }
                                                onCreateOption={(value) =>
                                                    handleCreateOption(key, value)
                                                }
                                                onKeyDown={(event) =>
                                                    handleSelectKeyDown(
                                                        key,
                                                        event,
                                                    )
                                                }
                                            />
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>
                    </div>
                </Collapsible.Content>
            </Collapsible.Root>

            <Collapsible.Root open={hasActiveFilters}>
                <Collapsible.Content className="notification-column-filters__chips-collapsible">
                    <div className="notification-column-filters__chips-shell">
                        <div
                            className="notification-column-filters__chips"
                            aria-label="Applied notification column filters"
                            aria-hidden={!hasActiveFilters}
                        >
                            {entriesForChips.map(({ key, label, value }) => (
                                <span
                                    key={key}
                                    className="notification-column-filters__chip"
                                >
                                    <span className="notification-column-filters__chip-text">
                                        <strong>{label}:</strong> {value}
                                    </span>
                                    <button
                                        type="button"
                                        className="notification-column-filters__chip-remove"
                                        aria-label={`Remove ${label} filter`}
                                        onClick={() => removeFilter(key)}
                                        disabled={!hasActiveFilters}
                                    >
                                        <LuX aria-hidden size={14} />
                                    </button>
                                </span>
                            ))}
                            <button
                                type="button"
                                className="notification-column-filters__clear"
                                onClick={clearAllFilters}
                                disabled={!hasActiveFilters}
                            >
                                Clear column filters
                            </button>
                        </div>
                    </div>
                </Collapsible.Content>
            </Collapsible.Root>
        </div>
    );
};

export default NotificationColumnFilters;
